import intl from 'react-intl-universal';
import { Button } from '@mui/material';
import {
  BillingMonthColumn,
  FromOrganizationNameColumn,
  DetailsColumn,
  TotalPriceColumn,
} from '../commonColumns';
import PropTypes from 'prop-types';
import {
  createBillingCSVContent,
  createDownloadLink,
  createCSVKenshinCSVContent,
} from '../shared/csv';
import logger from 'utils/logger';

const log = logger.createLogger('SummaryTableReceived');

export const DownloadColumn = (
  billData,
  billDataKenshin,
  doesHaveKenshinPermission
) => {
  const exportToCSV = (organizationId, organizationName, billingMonth) => {
    const selectedBillData = billData.find(
      (bill) =>
        bill.orgId === organizationId && bill.billedMonthYear === billingMonth
    );

    const filteredSelectedBillData = selectedBillData
      ? selectedBillData.bills
      : [];

    const csvFilename = `normal-bills-to-${organizationName}-${billingMonth}.csv`;
    const csvContent = createBillingCSVContent(filteredSelectedBillData, true);
    createDownloadLink(csvContent, csvFilename);
  };

  const exportToCSVKenshin = (
    organizationId,
    organizationName,
    billingMonth
  ) => {
    const selectedBillData = billDataKenshin.find(
      (bill) =>
        bill.orgId === organizationId && bill.billedMonthYear === billingMonth
    );

    const filteredSelectedBillData = selectedBillData
      ? selectedBillData.bills
      : [];

    const csvFilename = `kenshin-bills-to-${organizationName}-${billingMonth}.csv`;
    const csvContent = createCSVKenshinCSVContent(
      filteredSelectedBillData,
      true
    );
    createDownloadLink(csvContent, csvFilename);
  };

  return {
    accessorKey: 'download',
    header: intl.get('billing_for_received_cases_label_column_download'),
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnActions: false,
    Cell: ({ renderedCellValue }) =>
      exportToCSVCell({
        renderedCellValue,
        exportToCSV,
        exportToCSVKenshin,
        doesHaveKenshinPermission,
      }),
  };
};

const exportToCSVCell = ({
  renderedCellValue,
  exportToCSV,
  exportToCSVKenshin,
  doesHaveKenshinPermission,
}) => {
  const { organizationId, organizationName, billingMonth } = renderedCellValue;
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          exportToCSV(organizationId, organizationName, billingMonth);
        }}>
        {intl.get('billing_for_received_cases_button_download_csv_seimitsu')}
      </Button>
      {doesHaveKenshinPermission && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            exportToCSVKenshin(organizationId, organizationName, billingMonth);
          }}>
          {intl.get('billing_for_received_cases_button_download_csv_kenshin')}
        </Button>
      )}
    </div>
  );
};

exportToCSVCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
  exportToCSV: PropTypes.func.isRequired,
  exportToCSVKenshin: PropTypes.func.isRequired,
  doesHaveKenshinPermission: PropTypes.bool.isRequired,
};

export const InvoiceColumn = (
  setInvoiceToOrgId,
  setInvoiceToOrgName,
  setInvoiceTotalAmount,
  setInvoiceEditorModalOpen,
  setInvoiceToBillDate
) => {
  return {
    accessorKey: 'invoice',
    header: intl.get('billing_for_received_cases_label_column_invoice'),
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnActions: false,
    Cell: ({ renderedCellValue }) =>
      invoiceCell({
        renderedCellValue,
        setInvoiceToOrgId,
        setInvoiceToOrgName,
        setInvoiceTotalAmount,
        setInvoiceEditorModalOpen,
        setInvoiceToBillDate,
      }),
  };
};

const invoiceCell = ({
  renderedCellValue,
  setInvoiceToOrgId,
  setInvoiceToOrgName,
  setInvoiceTotalAmount,
  setInvoiceEditorModalOpen,
  setInvoiceToBillDate,
}) => {
  const { organizationId, organizationName, totalAmount, billingMonth } =
    renderedCellValue;
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setInvoiceToOrgId(organizationId);
          setInvoiceToOrgName(organizationName);
          setInvoiceTotalAmount(totalAmount);
          setInvoiceEditorModalOpen(true);
          setInvoiceToBillDate(billingMonth);
        }}>
        {intl.get('billing_for_received_cases_button_download_invoice_pdf')}
      </Button>
    </div>
  );
};

invoiceCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
  setInvoiceToOrgId: PropTypes.func.isRequired,
  setInvoiceToOrgName: PropTypes.func.isRequired,
  setInvoiceTotalAmount: PropTypes.func.isRequired,
  setInvoiceEditorModalOpen: PropTypes.func.isRequired,
  setInvoiceToBillDate: PropTypes.func.isRequired,
};

export const summaryTableColumns = (
  setSelectedSrcOrgName,
  setSelectedSrcOrgId,
  setTableType,
  billData,
  billDataKenshin,
  setInvoiceToOrgId,
  setInvoiceToOrgName,
  setInvoiceTotalAmount,
  setInvoiceEditorModalOpen,
  doesHaveKenshinPermission,
  setSelectedBillingMonth,
  setInvoiceToBillDate
) => {
  return [
    BillingMonthColumn(),
    FromOrganizationNameColumn(),
    TotalPriceColumn(),
    DetailsColumn(
      setSelectedBillingMonth,
      setSelectedSrcOrgName,
      setSelectedSrcOrgId,
      setTableType,
      doesHaveKenshinPermission
    ),
    DownloadColumn(billData, billDataKenshin, doesHaveKenshinPermission),
    InvoiceColumn(
      setInvoiceToOrgId,
      setInvoiceToOrgName,
      setInvoiceTotalAmount,
      setInvoiceEditorModalOpen,
      setInvoiceToBillDate
    ),
  ];
};

export const getSummaryTableData = (billData, billDataKenshin) => {
  const totalBillMap = new Map();

  const mergedBills = [...billData, ...billDataKenshin];

  mergedBills.forEach((bill) => {
    const { billedMonthYear, orgName, orgId, billedFee, startDate, endDate } =
      bill;
    const totalBill = totalBillMap.get(`${orgId}+${billedMonthYear}`);
    if (!totalBill) {
      totalBillMap.set(`${orgId}+${billedMonthYear}`, {
        billedMonthYear,
        startDate,
        endDate,
        orgName,
        orgId,
        billedFee,
      });
    } else {
      totalBillMap.set(`${orgId}+${billedMonthYear}`, {
        billedMonthYear,
        startDate,
        endDate,
        orgName,
        orgId,
        billedFee: totalBill.billedFee + billedFee,
      });
    }
  });

  const sortedTotalBillMap = Array.from(totalBillMap.values()).sort((a, b) => {
    const [monthA, yearA] = a.billedMonthYear.split('-');
    const [monthB, yearB] = b.billedMonthYear.split('-');
    return (
      parseInt(yearA) - parseInt(yearB) || parseInt(monthA) - parseInt(monthB)
    );
  });

  const data = [];
  sortedTotalBillMap.forEach((value) => {
    const { billedMonthYear, orgName, orgId, billedFee, startDate, endDate } =
      value;
    data.push({
      billingMonth: {
        startDate,
        endDate,
      },
      fromOrganizationName: orgName,
      totalPrice: billedFee,
      details: {
        organizationName: orgName,
        organizationId: orgId,
        billingMonth: billedMonthYear,
      },
      download: {
        organizationName: orgName,
        organizationId: orgId,
        billingMonth: billedMonthYear,
      },
      invoice: {
        organizationName: orgName,
        organizationId: orgId,
        totalAmount: billedFee,
        billingMonth: billedMonthYear,
      },
    });
  });

  return data;
};
